/**
 * ClientSidebar — Colonne gauche persistante sur toutes les pages client
 *
 * Affiche : données naissance, contact, notes, documents,
 * TN, RS, Progressions, Transits, Groupes.
 */

import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '../../utils/api.js';
import { colors } from '../../styles/tokens.js';
import { useClient } from '../../contexts/ClientContext.js';
import { GroupeBloc } from './GroupeBloc.js';

interface SeanceSummary {
  id: number;
  date: string;
  apercu: string | null;
}

const FILE_TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  theme_natal_roue: { label: 'Theme Natal (Roue)', icon: 'bi-pie-chart', color: '#2563eb' },
  tableau_planetes: { label: 'Tableau Planetes', icon: 'bi-table', color: '#16a34a' },
  tableau_aspects: { label: 'Tableau Aspects', icon: 'bi-diagram-3', color: '#9333ea' },
  listing_aspects: { label: 'Listing Aspects', icon: 'bi-list-ul', color: '#dc2626' },
  tableau_dignites: { label: 'Tableau Dignites', icon: 'bi-award', color: '#ca8a04' },
  autre: { label: 'Autre', icon: 'bi-file-pdf', color: '#666' },
  theme_natal: { label: 'Theme Natal', icon: 'bi-stars', color: '#2563eb' },
  revolution_solaire: { label: 'Revolution Solaire', icon: 'bi-sun', color: '#16a34a' },
};

const sideCard: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  padding: '1.5rem',
  marginBottom: '1.5rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

export function ClientSidebar() {
  const { id } = useParams<{ id: string }>();
  const {
    client, selectedChartId, loadAnalysis,
    handleDeleteChart, handleDeleteFile, formatDate,
  } = useClient();

  const [seances, setSeances] = useState<SeanceSummary[]>([]);

  useEffect(() => {
    if (!id) return;
    apiFetch<SeanceSummary[]>(`/clients/${id}/seances`)
      .then((res) => { if (res.success && res.data) setSeances(res.data); });
  }, [id]);

  if (!client) return null;

  return (
    <div style={{ width: 300, flexShrink: 0 }}>
      {/* Données de naissance */}
      <div style={sideCard}>
        <h3 style={{ color: colors.text, marginBottom: '1rem', fontSize: '1rem', margin: '0 0 1rem' }}>
          <i className="bi bi-calendar-event" style={{ color: colors.accent, marginRight: 8 }} />
          Donnees de naissance
        </h3>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: colors.textMid }}>Date</span>
            <strong>{formatDate(client.dateNaissance)}</strong>
          </div>
          {client.heureNaissance && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: colors.textMid }}>Heure</span>
              <strong>{client.heureNaissance}</strong>
            </div>
          )}
          {client.lieuNaissance && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: colors.textMid }}>Lieu</span>
              <strong>{client.lieuNaissance}</strong>
            </div>
          )}
          {client.paysNaissance && client.paysNaissance !== 'France' && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: colors.textMid }}>Pays</span>
              <strong>{client.paysNaissance}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Contact */}
      {(client.email || client.telephone) && (
        <div style={sideCard}>
          <h3 style={{ color: colors.text, margin: '0 0 1rem', fontSize: '1rem' }}>
            <i className="bi bi-person-lines-fill" style={{ color: colors.accent, marginRight: 8 }} />
            Contact
          </h3>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {client.email && (
              <a href={`mailto:${client.email}`} style={{ color: colors.text, textDecoration: 'none', fontSize: '0.9rem' }}>
                <i className="bi bi-envelope" style={{ color: colors.accent, marginRight: 8 }} />
                {client.email}
              </a>
            )}
            {client.telephone && (
              <a href={`tel:${client.telephone}`} style={{ color: colors.text, textDecoration: 'none', fontSize: '0.9rem' }}>
                <i className="bi bi-telephone" style={{ color: colors.accent, marginRight: 8 }} />
                {client.telephone}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Notes */}
      {client.notes && (
        <div style={sideCard}>
          <h3 style={{ color: colors.text, margin: '0 0 1rem', fontSize: '1rem' }}>
            <i className="bi bi-journal-text" style={{ color: colors.accent, marginRight: 8 }} />
            Notes
          </h3>
          <p style={{ color: colors.text, whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.9rem' }}>
            {client.notes}
          </p>
        </div>
      )}

      {/* Documents */}
      <div style={sideCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: colors.text, margin: 0, fontSize: '1rem' }}>
            <i className="bi bi-folder" style={{ color: colors.accent, marginRight: 8 }} />
            Documents
          </h3>
          <Link
            to={`/clients/${id}/import`}
            style={{
              width: 28, height: 28, borderRadius: '50%',
              background: colors.accent, color: colors.text,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none', fontSize: '1rem', fontWeight: 700, lineHeight: 1,
            }}
            title="Ajouter un document"
          >
            +
          </Link>
        </div>
        {client.fichiers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: colors.textMuted, fontSize: '0.85rem' }}>
            <i className="bi bi-folder2-open" /> Aucun document
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {client.fichiers.map((fichier) => {
              const typeInfo = (fichier.type ? FILE_TYPE_LABELS[fichier.type] : null) ?? { label: fichier.type ?? 'Document', icon: 'bi-file-pdf', color: '#666' };
              return (
                <div key={fichier.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem', background: colors.bgPage, borderRadius: 6, fontSize: '0.85rem',
                  minWidth: 0,
                }}>
                  <i className={`bi ${typeInfo.icon}`} style={{ color: typeInfo.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {fichier.nomOriginal ?? typeInfo.label}
                  </span>
                  <i className="bi bi-eye" role="button" aria-label="Voir le document" style={{ color: colors.accent, cursor: 'pointer', flexShrink: 0 }} />
                  <i
                    className="bi bi-trash"
                    role="button"
                    aria-label="Supprimer le document"
                    onClick={() => handleDeleteFile(fichier.id)}
                    style={{ color: colors.danger, cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0 }}
                    title="Supprimer"
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Thème Natal */}
      {client.charts.some((c) => c.type === 'NATAL') && (
        <div style={sideCard}>
          <h3 style={{ color: colors.text, margin: '0 0 1rem', fontSize: '1rem' }}>
            <i className="bi bi-stars" style={{ color: colors.accent, marginRight: 8 }} />
            Thème Natal
          </h3>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {client.charts.filter((c) => c.type === 'NATAL').map((chart) => (
              <div
                key={chart.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.5rem 0.75rem', borderRadius: 6,
                  background: selectedChartId === chart.id ? colors.accentLight : colors.bgPage,
                }}
              >
                <button
                  onClick={() => loadAnalysis(chart.id)}
                  aria-expanded={selectedChartId === chart.id}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    border: 'none', background: 'none',
                    cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit', textAlign: 'left',
                    padding: 0,
                  }}
                >
                  <span style={{ fontWeight: selectedChartId === chart.id ? 600 : 400 }}>
                    Theme natal
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                    {formatDate(chart.createdAt)}
                  </span>
                </button>
                <i
                  className="bi bi-trash"
                  role="button"
                  aria-label="Supprimer"
                  onClick={() => handleDeleteChart(chart.id)}
                  style={{ color: colors.danger, cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0, marginLeft: 4 }}
                  title="Supprimer"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Révolutions Solaires */}
      <div style={sideCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: colors.text, margin: 0, fontSize: '1rem' }}>
            <i className="bi bi-sun" style={{ color: '#16a34a', marginRight: 8 }} />
            Révolutions Solaires
          </h3>
          <Link
            to={`/clients/${id}/import`}
            style={{
              width: 28, height: 28, borderRadius: '50%',
              background: '#16a34a', color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none', fontSize: '1rem', fontWeight: 700, lineHeight: 1,
            }}
            title="Importer une RS"
          >
            +
          </Link>
        </div>
        {(() => {
          const rsCharts = client.charts
            .filter((c) => c.type === 'REVOLUTION_SOLAIRE')
            .sort((a, b) => (b.anneeRs ?? 0) - (a.anneeRs ?? 0));

          if (rsCharts.length === 0) {
            return (
              <div style={{ textAlign: 'center', padding: '0.75rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                <i className="bi bi-sun" /> Aucune RS importée
              </div>
            );
          }

          const hasTN = client.charts.some((c) => c.type === 'NATAL');

          return (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {rsCharts.map((chart) => (
                <div
                  key={chart.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.5rem 0.75rem', borderRadius: 6, background: colors.bgPage,
                  }}
                >
                  <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 500 }}>
                    RS {chart.anneeRs ?? '?'}
                  </span>
                  {hasTN && (
                    <Link
                      to={`/clients/${id}/rs/${chart.id}`}
                      style={{ color: colors.accent, fontSize: '0.8rem', textDecoration: 'none', marginRight: 4 }}
                      title="Voir la superposition RS/TN"
                    >
                      <i className="bi bi-layers" />
                    </Link>
                  )}
                  <button
                    onClick={() => loadAnalysis(chart.id)}
                    aria-expanded={selectedChartId === chart.id}
                    style={{
                      border: 'none', background: 'none', cursor: 'pointer',
                      color: colors.textMid, fontSize: '0.8rem', padding: 0,
                    }}
                    title="Voir les positions RS seules"
                  >
                    <i className="bi bi-eye" />
                  </button>
                  <i
                    className="bi bi-trash"
                    onClick={() => handleDeleteChart(chart.id)}
                    style={{ color: colors.danger, cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0, marginLeft: 4 }}
                    title="Supprimer"
                  />
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Progressions */}
      <div style={sideCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: colors.text, margin: 0, fontSize: '1rem' }}>
            <i className="bi bi-clock-history" style={{ color: '#7c3aed', marginRight: 8 }} />
            Progressions
          </h3>
          <Link
            to={`/clients/${id}/import`}
            style={{
              width: 28, height: 28, borderRadius: '50%',
              background: '#7c3aed', color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none', fontSize: '1rem', fontWeight: 700, lineHeight: 1,
            }}
            title="Importer des progressions"
          >
            +
          </Link>
        </div>
        {(() => {
          const progCharts = client.charts
            .filter((c) => c.type === 'PROGRESSIONS')
            .sort((a, b) => (b.anneeRs ?? 0) - (a.anneeRs ?? 0));

          if (progCharts.length === 0) {
            return (
              <div style={{ textAlign: 'center', padding: '0.75rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                <i className="bi bi-clock-history" /> Aucune progression importée
              </div>
            );
          }

          const hasTN = client.charts.some((c) => c.type === 'NATAL');

          return (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {progCharts.map((chart) => (
                <div
                  key={chart.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.5rem 0.75rem', borderRadius: 6, background: colors.bgPage,
                  }}
                >
                  <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 500 }}>
                    Prog {chart.anneeRs ?? '?'}
                  </span>
                  {hasTN && (
                    <Link
                      to={`/clients/${id}/prog/${chart.id}`}
                      style={{ color: '#7c3aed', fontSize: '0.8rem', textDecoration: 'none', marginRight: 4 }}
                      title="Voir la superposition Prog/TN"
                    >
                      <i className="bi bi-layers" />
                    </Link>
                  )}
                  <button
                    onClick={() => loadAnalysis(chart.id)}
                    aria-expanded={selectedChartId === chart.id}
                    style={{
                      border: 'none', background: 'none', cursor: 'pointer',
                      color: colors.textMid, fontSize: '0.8rem', padding: 0,
                    }}
                    title="Voir les positions progressées seules"
                  >
                    <i className="bi bi-eye" />
                  </button>
                  <i
                    className="bi bi-trash"
                    onClick={() => handleDeleteChart(chart.id)}
                    style={{ color: colors.danger, cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0, marginLeft: 4 }}
                    title="Supprimer"
                  />
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Transits */}
      <div style={sideCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: colors.text, margin: 0, fontSize: '1rem' }}>
            <i className="bi bi-arrow-repeat" style={{ color: '#0891b2', marginRight: 8 }} />
            Transits
          </h3>
          <Link
            to={`/clients/${id}/import`}
            style={{
              width: 28, height: 28, borderRadius: '50%',
              background: '#0891b2', color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none', fontSize: '1rem', fontWeight: 700, lineHeight: 1,
            }}
            title="Importer un PDF de transits"
          >
            +
          </Link>
        </div>
        {(() => {
          const transitCharts = client.charts
            .filter((c) => c.type === 'TRANSITS')
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          if (transitCharts.length === 0) {
            return (
              <div style={{ textAlign: 'center', padding: '0.75rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                <i className="bi bi-arrow-repeat" /> Aucun transit importé
              </div>
            );
          }

          return (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {transitCharts.map((chart) => (
                <div
                  key={chart.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.5rem 0.75rem', borderRadius: 6, background: colors.bgPage,
                  }}
                >
                  <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 500 }}>
                    Transits
                  </span>
                  <span style={{ fontSize: '0.75rem', color: colors.textMuted, marginRight: 4 }}>
                    {formatDate(chart.createdAt)}
                  </span>
                  <Link
                    to={`/clients/${id}/transits/${chart.id}`}
                    style={{ color: '#0891b2', fontSize: '0.8rem', textDecoration: 'none', marginRight: 4 }}
                    title="Consulter les transits"
                  >
                    <i className="bi bi-eye" />
                  </Link>
                  <i
                    className="bi bi-trash"
                    onClick={() => handleDeleteChart(chart.id)}
                    style={{ color: colors.danger, cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0, marginLeft: 4 }}
                    title="Supprimer"
                  />
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Séances */}
      <div style={sideCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: colors.text, margin: 0, fontSize: '1rem' }}>
            <i className="bi bi-journal-richtext" style={{ color: '#0891b2', marginRight: 8 }} />
            Séances
          </h3>
          <Link
            to={`/clients/${id}/seances/nouveau`}
            style={{
              width: 28, height: 28, borderRadius: '50%',
              background: '#0891b2', color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              textDecoration: 'none', fontSize: '1rem', fontWeight: 700, lineHeight: 1,
            }}
            title="Nouvelle séance"
          >
            +
          </Link>
        </div>
        {seances.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '0.75rem', color: colors.textMuted, fontSize: '0.85rem' }}>
            <i className="bi bi-journal" /> Aucune séance
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {seances.slice(0, 10).map((s) => (
              <Link
                key={s.id}
                to={`/clients/${id}/seances/${s.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.75rem', borderRadius: 6, background: colors.bgPage,
                  textDecoration: 'none', color: colors.text, fontSize: '0.85rem',
                }}
              >
                <span style={{ fontWeight: 500, flexShrink: 0 }}>
                  {formatDate(s.date)}
                </span>
                {s.apercu && (
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.textMuted, fontSize: '0.75rem' }}>
                    {s.apercu}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Groupes familiaux / relationnels */}
      <GroupeBloc clientId={client.id} clientNom={client.nom} clientPrenom={client.prenom} />
    </div>
  );
}
